// Unsaved quantity edits on order cards: product id -> target quantity.
const orderCartDataElement = document.getElementById('dhs-order-cart-data');

if (orderCartDataElement) {
    // Sparse map: product ID -> edited target quantity not yet saved to the cart.
    const draftCartQuantities = {};
    const orderCartData = JSON.parse(orderCartDataElement.textContent);
    const productStock = orderCartData.productStock || {};
    const kitRecipes = orderCartData.kitRecipes || {};
    const savedQuantities = orderCartData.savedQuantities || {};
    const enabledCartActions = Array.isArray(orderCartData.enabledCartActions)
        ? new Set(orderCartData.enabledCartActions)
        : new Set(['add', 'set', 'remove']);
    const pendingProductSets = new Set();
    const pendingProductRemovals = new Set();
    let pendingCartWriteCount = 0;
    let pendingCartWritesSettled = Promise.resolve();
    let resolvePendingCartWrites = null;
    let isNextStepRunning = false;
    let isPlaceOrderRunning = false;

    /** Tracks the start of a cart write so Next can wait for it. */
    function beginCartWrite() {
        if (pendingCartWriteCount === 0) {
            pendingCartWritesSettled = new Promise(function (resolve) {
                resolvePendingCartWrites = resolve;
            });
        }
        pendingCartWriteCount++;
    }

    /** Resolves the pending-write wait when the final cart write settles. */
    function finishCartWrite() {
        pendingCartWriteCount--;
        if (pendingCartWriteCount === 0 && resolvePendingCartWrites) {
            resolvePendingCartWrites();
            resolvePendingCartWrites = null;
        }
    }

    /** Waits until all currently pending cart writes have settled.
     * @returns {Promise<void>}
     */
    async function waitForCartWrites() {
        while (pendingCartWriteCount > 0) {
            await pendingCartWritesSettled;
        }
    }

    /** Updates the Device Next link according to the saved cart contents. */
    function updateNextStepAvailability() {
        const nextLink = document.querySelector('[data-order-cart-next]');
        if (!nextLink) {
            return;
        }

        const hasCartItems = Object.values(savedQuantities).some(function (quantity) {
            return Number(quantity) > 0;
        });
        nextLink.classList.toggle('disabled', !hasCartItems);
        const nextButton = nextLink.querySelector('.btn');
        if (nextButton) {
            nextButton.classList.toggle('disabled', !hasCartItems);
        }
        if (hasCartItems) {
            nextLink.href = nextLink.dataset.nextHref;
            nextLink.removeAttribute('aria-disabled');
            nextLink.removeAttribute('tabindex');
        } else {
            nextLink.removeAttribute('href');
            nextLink.setAttribute('aria-disabled', 'true');
            nextLink.setAttribute('tabindex', '-1');
        }
    }

    /** Gets the selected quantity and remaining stock for a regular card.
     * @param {HTMLElement} card Regular-product card.
     * @returns {{productId: string, stockBaseline: number, displayedQty: number, remainingStock: number}}
     */
    function getRegularStockState(card) {
        const productId = card.dataset.productId;
        const stockBaseline = Number(card.dataset.stockBaseline);
        const inCartSavedQty = Number(card.dataset.inCartSavedQty);
        const hasDraft = Object.prototype.hasOwnProperty.call(draftCartQuantities, productId);
        const displayedQty = hasDraft ? Number(draftCartQuantities[productId]) : inCartSavedQty;

        return {
            productId: productId,
            stockBaseline: stockBaseline,
            displayedQty: displayedQty,
            remainingStock: Math.max(0, stockBaseline - displayedQty),
        };
    }

    /** Renders the calculated stock and control state on a regular card.
     * @param {HTMLElement} card Regular-product card.
     * @param {number} [remainingStockOverride] Remaining stock after shared kit usage.
     * @returns {void}
     */
    function updateRegularStockPreview(card, remainingStockOverride) {
        const state = getRegularStockState(card);
        const remainingStock = remainingStockOverride ?? state.remainingStock;
        const hasRemainingStock = remainingStock > 0;
        const stockDisplay = card.querySelector('[data-stock-display]');
        const addButton = card.querySelector('[data-product-action="add"]');
        const quantityInput = card.querySelector('[data-quantity-input]');
        const plusButton = card.querySelector('[data-qty-step="1"]');
        const minusButton = card.querySelector('[data-qty-step="-1"]');

        stockDisplay.textContent = hasRemainingStock
            ? remainingStock + ' in stock'
            : 'Out of stock';
        stockDisplay.classList.toggle('text-primary', hasRemainingStock);
        stockDisplay.classList.toggle('text-danger', !hasRemainingStock);

        if (addButton) {
            addButton.disabled = !hasRemainingStock || state.displayedQty > 0;
        }
        if (quantityInput) {
            quantityInput.max = state.stockBaseline;
        }
        if (plusButton) {
            plusButton.disabled = !hasRemainingStock;
        }
        if (minusButton) {
            minusButton.disabled = state.displayedQty <= 1;
        }

    }

    /** Adds, updates, or clears the sparse draft for one quantity form.
     * @param {HTMLFormElement} form Quantity form.
     * @returns {void}
     */
    function refreshCard(form) {
        const input = form.querySelector('input[name="quantity"]');
        const setButton = form.querySelector('[data-set-button]');
        const id = form.dataset.itemId;
        const value = parseInt(input.value, 10);
        const inCartSavedQty = parseInt(form.dataset.inCartSavedQty || savedQuantities[id] || 0, 10);

        if (value === inCartSavedQty) {
            delete draftCartQuantities[id];
        } else {
            draftCartQuantities[id] = value;
        }

        setButton.disabled = !(id in draftCartQuantities);
    }

    /** Copies one draft quantity to all responsive forms for the same product.
     * @param {HTMLFormElement} form Changed quantity form.
     * @returns {void}
     */
    function refreshQuantityCopies(form) {
        const productId = form.dataset.itemId;
        const value = form.querySelector('input[name="quantity"]').value;

        document.querySelectorAll('[data-qty-form]').forEach(function (quantityForm) {
            if (quantityForm.dataset.itemId !== productId) {
                return;
            }

            quantityForm.querySelector('input[name="quantity"]').value = value;
            refreshCard(quantityForm);
        });
    }

    /** Finds the regular or kit card copies for a cart product ID.
     * @param {string} productId Cart product or kit ID.
     * @returns {HTMLElement[]} Matching responsive card elements.
     */
    function getCartCards(productId) {
        const escapedProductId = CSS.escape(String(productId));

        return Array.from(document.querySelectorAll(
            '[data-product-stock-card][data-product-id="' + escapedProductId + '"], '
            + '[data-kit-stock-card][data-kit-id="' + escapedProductId + '"]'
        ));
    }

    /** Updates saved quantities and inputs across responsive card copies.
     * @param {string} productId Cart product or kit ID.
     * @param {number} quantity Confirmed saved quantity.
     * @returns {void}
     */
    function syncSavedQuantityCopies(productId, quantity) {
        savedQuantities[productId] = quantity;
        getCartCards(productId).forEach(function (card) {
            const isKit = card.matches('[data-kit-stock-card]');
            if (isKit) {
                card.dataset.kitInCartSavedQty = String(quantity);
            } else {
                card.dataset.inCartSavedQty = String(quantity);
            }

            const form = card.querySelector('[data-qty-form]');
            if (!form) {
                return;
            }

            form.dataset.inCartSavedQty = String(quantity);
            const input = form.querySelector('input[name="quantity"]');
            if (input) {
                input.value = String(quantity);
            }
            refreshCard(form);
        });
    }

    /** Rebuilds quantity drafts and recalculates regular and kit previews.
     * @returns {void}
     */
    function refreshAllCards() {
        document.querySelectorAll('[data-qty-form]').forEach(refreshCard);
        updateStockPreviews('initial load');
    }

    /** Calculates provisional shared-component and kit availability.
     * @returns {{components: Object<string, {baseline: number, standaloneDesiredQty: number, selectedKitUsage: number, remaining: number}>, kits: Array<{kitId: string, phpStockBaseline: number, selectedQty: number, additionalAvailable: number|null, provisionalAvailable: number|null, invalid: boolean, missingComponents: string[]}>}}
     */
    function calculateProvisionalKitStock() {
        // TBC with ServiceNow: provisional kit preview uses regular-card stock for matched components.
        const kitCards = Array.from(document.querySelectorAll('[data-kit-stock-card]'));
        const regularCards = Array.from(document.querySelectorAll('[data-product-stock-card]'));
        const componentBaselines = Object.assign({}, productStock);
        const standaloneDesiredQty = {};
        const selectedKitQty = {};
        const componentUsage = {};
        const missingComponentsByKit = {};

        /** Gets a product's draft or saved quantity, with a card fallback.
         * @param {string} productId Product or kit ID.
         * @param {number|string} [fallbackQuantity] Quantity from the card markup.
         * @returns {number}
         */
        function getDisplayedQuantity(productId, fallbackQuantity) {
            if (Object.prototype.hasOwnProperty.call(draftCartQuantities, productId)) {
                return Number(draftCartQuantities[productId]);
            }

            return Number(savedQuantities[productId] ?? fallbackQuantity ?? 0);
        }

        for (const card of regularCards) {
            const productId = card.dataset.productId;
            standaloneDesiredQty[productId] = getDisplayedQuantity(
                productId,
                card.dataset.inCartSavedQty
            );

            if (Object.prototype.hasOwnProperty.call(productStock, productId)) {
                componentBaselines[productId] = Number(card.dataset.stockBaseline);
            }
        }

        for (const card of kitCards) {
            const kitId = card.dataset.kitId;
            selectedKitQty[kitId] = getDisplayedQuantity(
                kitId,
                card.dataset.kitInCartSavedQty
            );
        }

        for (const productId of Object.keys(componentBaselines)) {
            componentUsage[productId] = {
                baseline: Number(componentBaselines[productId]),
                standaloneDesiredQty: Number(standaloneDesiredQty[productId] || 0),
                selectedKitUsage: 0,
            };
        }

        for (const card of kitCards) {
            const kitId = card.dataset.kitId;
            const recipe = kitRecipes[kitId] || [];
            const selectedQty = selectedKitQty[kitId] || 0;
            const missingComponents = [];

            for (const component of recipe) {
                const productId = component.itemID;
                if (!Object.prototype.hasOwnProperty.call(componentUsage, productId)) {
                    missingComponents.push(productId);
                    continue;
                }

                componentUsage[productId].selectedKitUsage += selectedQty * Number(component.itemQty);
            }

            if (missingComponents.length > 0) {
                missingComponentsByKit[kitId] = missingComponents;
            }
        }

        const componentResults = {};
        for (const [productId, usage] of Object.entries(componentUsage)) {
            const remaining = Math.max(
                0,
                usage.baseline - usage.standaloneDesiredQty - usage.selectedKitUsage
            );
            componentResults[productId] = {
                baseline: usage.baseline,
                standaloneDesiredQty: usage.standaloneDesiredQty,
                selectedKitUsage: usage.selectedKitUsage,
                remaining: remaining,
            };
        }

        const kitResults = kitCards.map(function (card) {
            const kitId = card.dataset.kitId;
            const recipe = kitRecipes[kitId] || [];
            const selectedQty = selectedKitQty[kitId] || 0;
            const isInvalid = card.dataset.kitValid !== '1'
                || card.dataset.kitRecipeInvalid === '1'
                || recipe.length === 0
                || Object.prototype.hasOwnProperty.call(missingComponentsByKit, kitId);

            let additionalAvailable = Infinity;
            if (!isInvalid) {
                for (const component of recipe) {
                    const componentRemaining = componentResults[component.itemID].remaining;
                    const componentQuantity = Number(component.itemQty);
                    additionalAvailable = Math.min(
                        additionalAvailable,
                        Math.floor(componentRemaining / componentQuantity)
                    );
                }
            }

            return {
                kitId: kitId,
                phpStockBaseline: Number(card.dataset.kitStockBaseline),
                selectedQty: selectedQty,
                additionalAvailable: isInvalid ? null : additionalAvailable,
                provisionalAvailable: isInvalid ? null : selectedQty + additionalAvailable,
                invalid: isInvalid,
                missingComponents: missingComponentsByKit[kitId] || [],
            };
        });

        return {
            components: componentResults,
            kits: kitResults,
        };
    }

    /** Renders provisional stock and quantity limits on a kit card.
     * @param {HTMLElement} card Kit card.
     * @param {{invalid: boolean, selectedQty: number, additionalAvailable: number|null, provisionalAvailable: number|null}} kitResult Calculated kit availability.
     * @returns {void}
     */
    function updateKitStockPreview(card, kitResult) {
        if (!kitResult || kitResult.invalid) {
            return;
        }

        const hasAdditionalStock = kitResult.additionalAvailable > 0;
        const stockDisplay = card.querySelector('[data-kit-stock-display]');
        const addButton = card.querySelector('[data-product-action="add"]');
        const quantityInput = card.querySelector('[data-kit-quantity-input]');
        const plusButton = card.querySelector('[data-qty-step="1"]');
        const minusButton = card.querySelector('[data-qty-step="-1"]');

        if (stockDisplay) {
            stockDisplay.textContent = hasAdditionalStock
                ? kitResult.additionalAvailable + ' in stock'
                : 'Out of stock';
            stockDisplay.classList.toggle('text-primary', hasAdditionalStock);
            stockDisplay.classList.toggle('text-danger', !hasAdditionalStock);
        }
        if (addButton) {
            addButton.disabled = !hasAdditionalStock;
        }
        if (quantityInput) {
            quantityInput.max = kitResult.provisionalAvailable;
        }
        if (plusButton) {
            plusButton.disabled = !hasAdditionalStock;
        }
        if (minusButton) {
            minusButton.disabled = kitResult.selectedQty <= 1;
        }
    }

    /** Recalculates and renders stock previews for all visible card copies.
     * @returns {void}
     */
    function updateStockPreviews() {
        const stockResults = calculateProvisionalKitStock();
        const kitResultsById = {};
        stockResults.kits.forEach(function (kitResult) {
            kitResultsById[kitResult.kitId] = kitResult;
        });

        for (const card of document.querySelectorAll('[data-product-stock-card]')) {
            const componentStock = stockResults.components[card.dataset.productId];
            updateRegularStockPreview(card, componentStock?.remaining);
        }

        for (const card of document.querySelectorAll('[data-kit-stock-card]')) {
            updateKitStockPreview(card, kitResultsById[card.dataset.kitId]);
        }

    }

    /** Handles a quantity step-button click and refreshes drafts and previews.
     * @param {MouseEvent} event Click event from a quantity step button.
     * @returns {void}
     */
    function handleQuantityStepButtonClick(event) {
        const stepButton = event.target.closest('[data-qty-step]');
        const form = stepButton ? stepButton.closest('[data-qty-form]') : null;

        if (!form) {
            return;
        }

        event.preventDefault();

        const input = form.querySelector('input[name="quantity"]');
        const newValue = parseInt(input.value, 10) + parseInt(stepButton.dataset.qtyStep, 10);

        if (newValue >= 1) {
            input.value = newValue;
        }

        refreshQuantityCopies(form);
        updateStockPreviews();
    }

    /** Handles typed quantity edits and refreshes drafts and previews.
     * @param {InputEvent} event Input event from a quantity field.
     * @returns {void}
     */
    function handleQuantityInput(event) {
        const form = event.target.closest('[data-qty-form]');

        if (form && event.target.name === 'quantity') {
            refreshQuantityCopies(form);
            updateStockPreviews();
        }
    }

    /** Displays a visible cart-save error for retry.
     * @param {string} message User-facing error message.
     * @returns {void}
     */
    function showCartSaveError(message) {
        const errorDisplay = document.querySelector('[data-order-cart-error]');
        if (!errorDisplay) {
            return;
        }

        errorDisplay.textContent = message;
        errorDisplay.classList.remove('d-none');
    }

    /** Saves a dirty regular-product or kit quantity through the cart REST endpoint.
     * @param {SubmitEvent} event Submit event from a quantity form.
     * @returns {Promise<void>}
     */
    async function handleProductSet(event) {
        const form = event.target.closest('[data-qty-form]');
        const card = form ? form.closest('[data-product-stock-card], [data-kit-stock-card]') : null;

        if (!card || !enabledCartActions.has('set')) {
            return;
        }

        event.preventDefault();

        const isKit = card.matches('[data-kit-stock-card]');
        const productId = isKit ? card.dataset.kitId : form.dataset.itemId;
        const input = form.querySelector('input[name="quantity"]');
        const targetQuantity = Number(input.value);

        if (
            pendingProductSets.has(productId)
            || pendingProductRemovals.has(productId)
            || !Object.prototype.hasOwnProperty.call(draftCartQuantities, productId)
        ) {
            return;
        }

        if (!form.checkValidity() || !Number.isInteger(targetQuantity) || targetQuantity < 1) {
            form.reportValidity();

            return;
        }

        const setButton = form.querySelector('[data-set-button]');
        const originalButtonText = setButton.textContent;
        const originalBusy = setButton.getAttribute('aria-busy');
        const relatedCards = getCartCards(productId);
        const controls = relatedCards.flatMap(function (relatedCard) {
            return Array.from(relatedCard.querySelectorAll('button, input, select, textarea'));
        });
        const originalDisabledStates = controls.map(function (control) {
            return control.disabled;
        });
        const errorDisplay = document.querySelector('[data-order-cart-error]');

        pendingProductSets.add(productId);
        beginCartWrite();
        controls.forEach(function (control) {
            control.disabled = true;
        });
        setButton.textContent = 'Saving...';
        setButton.setAttribute('aria-busy', 'true');
        if (errorDisplay) {
            errorDisplay.classList.add('d-none');
            errorDisplay.textContent = '';
        }

        try {
            const response = await fetch(orderCartData.restUrl, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': window.WP_API_Settings.nonce,
                },
                body: JSON.stringify({
                    action: 'set',
                    productId: productId,
                    quantity: targetQuantity,
                }),
            });
            const result = await response.json();

            if (!response.ok || !result.ok) {
                throw new Error('The quantity could not be saved. Please retry.');
            }

            const savedQuantity = Number(result.saved && result.saved[productId]);
            if (!Number.isFinite(savedQuantity) || savedQuantity !== targetQuantity) {
                throw new Error('The save could not be confirmed. Please retry.');
            }

            syncSavedQuantityCopies(productId, savedQuantity);
            updateStockPreviews();

        } catch (error) {
            showCartSaveError('The quantity could not be saved. Please retry.');
        } finally {
            controls.forEach(function (control, index) {
                control.disabled = originalDisabledStates[index];
            });
            setButton.textContent = originalButtonText;
            if (originalBusy === null) {
                setButton.removeAttribute('aria-busy');
            } else {
                setButton.setAttribute('aria-busy', originalBusy);
            }
            pendingProductSets.delete(productId);
            finishCartWrite();
            relatedCards.forEach(function (relatedCard) {
                const relatedForm = relatedCard.querySelector('[data-qty-form]');
                if (relatedForm) {
                    refreshCard(relatedForm);
                }
            });
            updateStockPreviews();
        }
    }

    /** Removes a cart line and updates both responsive card copies on confirmation.
     * @param {SubmitEvent} event Submit event from a remove form.
     * @returns {Promise<void>}
     */
    async function handleProductRemove(event) {
        const form = event.target.closest('[data-cart-remove-form]');
        const card = form ? form.closest('[data-product-stock-card], [data-kit-stock-card]') : null;

        if (!card || !enabledCartActions.has('remove')) {
            return;
        }

        event.preventDefault();

        const isKit = card.matches('[data-kit-stock-card]');
        const productId = isKit ? card.dataset.kitId : card.dataset.productId;
        if (pendingProductSets.has(productId) || pendingProductRemovals.has(productId)) {
            return;
        }

        const relatedCards = getCartCards(productId);
        const removeButton = form.querySelector('button[type="submit"]');
        const originalButtonText = removeButton.textContent;
        const originalBusy = removeButton.getAttribute('aria-busy');
        const controls = relatedCards.flatMap(function (relatedCard) {
            return Array.from(relatedCard.querySelectorAll('button, input, select, textarea'));
        });
        const originalDisabledStates = controls.map(function (control) {
            return control.disabled;
        });
        const errorDisplay = document.querySelector('[data-order-cart-error]');

        pendingProductRemovals.add(productId);
        beginCartWrite();
        controls.forEach(function (control) {
            control.disabled = true;
        });
        removeButton.textContent = 'Removing...';
        removeButton.setAttribute('aria-busy', 'true');
        if (errorDisplay) {
            errorDisplay.classList.add('d-none');
            errorDisplay.textContent = '';
        }

        if (Array.isArray(orderCartData.enabledCartActions)) {
            console.info('[order-cart][Step 10.2][TBC] Sending Checkout Remove', {
                productId: productId,
            });
        }

        try {
            const response = await fetch(orderCartData.restUrl, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': window.WP_API_Settings.nonce,
                },
                body: JSON.stringify({
                    action: 'remove',
                    productId: productId,
                }),
            });
            const result = await response.json();

            if (!response.ok || !result.ok || Number(result.saved && result.saved[productId]) !== 0) {
                throw new Error('The product could not be removed. Please retry.');
            }

            savedQuantities[productId] = 0;
            delete draftCartQuantities[productId];
            relatedCards.forEach(function (relatedCard) {
                if (orderCartData.page === 'checkout') {
                    relatedCard.classList.add('d-none');

                    return;
                }

                if (isKit) {
                    relatedCard.dataset.kitInCartSavedQty = '0';
                } else {
                    relatedCard.dataset.inCartSavedQty = '0';
                }

                const selectedControls = relatedCard.querySelector(
                    isKit ? '[data-kit-selected-controls]' : '[data-selected-product-controls]'
                );
                if (selectedControls) {
                    selectedControls.classList.add('d-none');
                }

                const addForm = relatedCard.querySelector('[data-product-add-form]');
                if (addForm) {
                    addForm.classList.remove('d-none');
                    const addLabel = addForm.querySelector('[data-add-label]');
                    if (addLabel) {
                        addLabel.textContent = 'Add to cart';
                    }
                }
            });
            if (orderCartData.page === 'checkout') {
                document.querySelectorAll('[data-checkout-cart-empty]').forEach(function (message) {
                    message.classList.toggle('d-none', !result.cartEmpty);
                });

                if (result.cartEmpty) {
                    const placeOrderButton = document.querySelector('#place-order-btn');
                    if (placeOrderButton) {
                        placeOrderButton.disabled = true;
                    }
                }
            }
            relatedCards.forEach(function (relatedCard) {
                const quantityForm = relatedCard.querySelector('[data-qty-form]');
                if (quantityForm) {
                    quantityForm.dataset.inCartSavedQty = '0';
                    quantityForm.querySelector('input[name="quantity"]').value = '1';
                    quantityForm.querySelector('[data-set-button]').disabled = true;
                }
            });
            updateStockPreviews();

            if (Array.isArray(orderCartData.enabledCartActions)) {
                console.info('[order-cart][Step 10.2][TBC] Checkout Remove confirmed', {
                    productId: productId,
                    cartEmpty: result.cartEmpty,
                });
            }
            updateNextStepAvailability();
        } catch (error) {
            showCartSaveError('The product could not be removed. Please retry.');
            if (Array.isArray(orderCartData.enabledCartActions)) {
                console.warn('[order-cart][Step 10.2][TBC] Checkout Remove failed', {
                    productId: productId,
                });
            }
        } finally {
            controls.forEach(function (control, index) {
                control.disabled = originalDisabledStates[index];
            });
            removeButton.textContent = originalButtonText;
            if (originalBusy === null) {
                removeButton.removeAttribute('aria-busy');
            } else {
                removeButton.setAttribute('aria-busy', originalBusy);
            }
            pendingProductRemovals.delete(productId);
            finishCartWrite();
            updateStockPreviews();
        }
    }

    /** Adds a regular product or kit through the cart REST endpoint.
     * @param {SubmitEvent} event Submit event from an Add form.
     * @returns {void}
     */
    function handleProductAdd(event) {
        const form = event.target.closest('[data-product-add-form]');
        if (!form || !enabledCartActions.has('add')) {
            return;
        }

        event.preventDefault();

        const formData = new FormData(form);
        const isKitAdd = formData.get('item_is_kit') === '1';
        const card = form.closest(isKitAdd ? '[data-kit-stock-card]' : '[data-product-stock-card]');
        const addButton = form.querySelector('[data-product-action="add"]');
        const addSpinner = addButton.querySelector('[data-add-spinner]');
        const addLabel = addButton.querySelector('[data-add-label]');
        const productId = isKitAdd ? card.dataset.kitId : card.dataset.productId;
        const stepLabel = isKitAdd ? 'Step 5.2' : 'Step 5.1';
        const errorDisplay = document.querySelector('[data-order-cart-error]');

        addButton.disabled = true;
        addButton.setAttribute('aria-busy', 'true');
        addSpinner.classList.remove('d-none');
        if (errorDisplay) {
            errorDisplay.classList.add('d-none');
            errorDisplay.textContent = '';
        }

        console.info('[order-cart][' + stepLabel + '][TBC] Sending Add', {
            productId: productId,
            targetQuantity: 1,
        });

        beginCartWrite();
        fetch(orderCartData.restUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-WP-Nonce': window.WP_API_Settings.nonce,
            },
            body: JSON.stringify({
                action: 'add',
                productId: productId,
                itemName: formData.get('item_name'),
                modelId: formData.get('item_model_id'),
                picture: formData.get('picture'),
                itemIsKit: isKitAdd,
            }),
        })
            .then(function (response) {
                return response.json().then(function (result) {
                    if (!response.ok || !result.ok) {
                        throw new Error(result.error || 'The product could not be saved. Please retry.');
                    }

                    return result;
                });
            })
            .then(function (result) {
                const savedQuantity = Number(result.saved[productId]);
                if (!Number.isFinite(savedQuantity)) {
                    throw new Error('The save could not be confirmed. Please retry.');
                }

                savedQuantities[productId] = savedQuantity;
                if (isKitAdd) {
                    card.dataset.kitInCartSavedQty = String(savedQuantity);
                } else {
                    card.dataset.inCartSavedQty = String(savedQuantity);
                }
                addButton.removeAttribute('aria-busy');
                addSpinner.classList.add('d-none');
                addLabel.textContent = 'Added to cart';
                const selectedControls = card.querySelector(
                    isKitAdd ? '[data-kit-selected-controls]' : '[data-selected-product-controls]'
                );
                const quantityForm = selectedControls.querySelector('[data-qty-form]');
                quantityForm.dataset.inCartSavedQty = String(savedQuantity);
                const quantityInput = quantityForm.querySelector(
                    isKitAdd ? '[data-kit-quantity-input]' : '[data-quantity-input]'
                );
                quantityInput.value = String(savedQuantity);
                quantityForm.querySelector('[data-set-button]').disabled = true;
                selectedControls.classList.remove('d-none');
                form.classList.add('d-none');
                updateStockPreviews();
                updateNextStepAvailability();

                console.info('[order-cart][' + stepLabel + '][TBC] Add confirmed', {
                    productId: productId,
                    inCartSavedQty: savedQuantity,
                    cartEmpty: result.cartEmpty,
                });
            })
            .catch(function (error) {
                addButton.removeAttribute('aria-busy');
                addSpinner.classList.add('d-none');
                addButton.disabled = false;
                showCartSaveError('The product could not be saved. Please retry.');
                console.warn('[order-cart][' + stepLabel + '][TBC] Add failed', {
                    productId: productId,
                    error: error.message,
                });
            })
            .finally(finishCartWrite);
    }

    /** Saves Checkout quantity drafts before submitting the order form.
     * @param {SubmitEvent} event Submit event from the Checkout form.
     * @returns {Promise<void>}
     */
    async function handleCheckoutPlaceOrder(event) {
        const form = event.target.closest('#submit-order-form');
        if (!form || orderCartData.page !== 'checkout') {
            return;
        }

        event.preventDefault();
        if (isPlaceOrderRunning) {
            return;
        }

        isPlaceOrderRunning = true;
        const placeOrderButton = document.querySelector('#place-order-btn');
        const placeOrderButtonLabel = placeOrderButton
            ? placeOrderButton.querySelector('[data-order-button-label]')
            : null;
        const placeOrderSpinner = placeOrderButton
            ? placeOrderButton.querySelector('[data-order-submit-spinner]')
            : null;
        const originalButtonText = placeOrderButtonLabel
            ? placeOrderButtonLabel.textContent
            : '';
        const originalButtonDisabled = placeOrderButton ? placeOrderButton.disabled : false;
        const errorDisplay = document.querySelector('[data-order-cart-error]');
        const batchControls = Array.from(document.querySelectorAll(
            '[data-product-stock-card], [data-kit-stock-card]'
        )).flatMap(function (card) {
            return Array.from(card.querySelectorAll('button, input, select, textarea'));
        });
        const originalBatchDisabledStates = batchControls.map(function (control) {
            return control.disabled;
        });
        let batchStarted = false;
        let orderSubmissionStarted = false;

        if (placeOrderButton) {
            placeOrderButton.disabled = true;
            if (placeOrderSpinner) {
                placeOrderSpinner.classList.remove('d-none');
            }
            placeOrderButton.setAttribute('aria-busy', 'true');
        }
        if (errorDisplay) {
            errorDisplay.classList.add('d-none');
            errorDisplay.textContent = '';
        }

        try {
            await waitForCartWrites();

            const targets = Object.assign({}, draftCartQuantities);
            if (Object.values(targets).some(function (quantity) {
                return !Number.isInteger(quantity) || quantity < 1;
            })) {
                throw new Error('Use Remove to remove a product. Quantities must be at least 1.');
            }

            let result = null;
            if (Object.keys(targets).length > 0) {
                batchControls.forEach(function (control) {
                    control.disabled = true;
                });

                beginCartWrite();
                batchStarted = true;
                const response = await fetch(orderCartData.restUrl, {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        'X-WP-Nonce': window.WP_API_Settings.nonce,
                    },
                    body: JSON.stringify({
                        action: 'batch',
                        quantities: targets,
                    }),
                });
                result = await response.json();

                if (!response.ok || !result.ok || !result.saved || typeof result.cartEmpty !== 'boolean') {
                    throw new Error('Cart changes could not be saved. Please retry.');
                }

                for (const [productId, quantity] of Object.entries(targets)) {
                    if (Number(result.saved[productId]) !== quantity) {
                        throw new Error('Cart changes could not be confirmed. Please retry.');
                    }
                }
                if (result.cartEmpty) {
                    throw new Error('Your cart is empty. Return to Device selection to add products.');
                }

                for (const [productId, quantity] of Object.entries(targets)) {
                    syncSavedQuantityCopies(productId, quantity);
                }
                updateStockPreviews();
            }

            const cartIsEmpty = result
                ? result.cartEmpty
                : !Object.values(savedQuantities).some(function (quantity) {
                    return Number(quantity) > 0;
                });
            if (cartIsEmpty) {
                document.querySelectorAll('[data-checkout-cart-empty]').forEach(function (message) {
                    message.classList.remove('d-none');
                });
                throw new Error('Your cart is empty. Return to Device selection to add products.');
            }

            orderSubmissionStarted = true;
            form.submit();
        } catch (error) {
            const message = error instanceof TypeError || error instanceof SyntaxError
                ? "We couldn't save your cart changes. Check your connection and try again. Your edits are still on this page."
                : error.message || 'Cart changes could not be saved. Please retry.';
            showCartSaveError(message);
        } finally {
            if (batchStarted) {
                finishCartWrite();
            }
            batchControls.forEach(function (control, index) {
                control.disabled = originalBatchDisabledStates[index];
            });
            if (placeOrderButton) {
                placeOrderButton.removeAttribute('aria-busy');
                if (!orderSubmissionStarted) {
                    if (placeOrderButtonLabel) {
                        placeOrderButtonLabel.textContent = originalButtonText;
                    }
                    if (placeOrderSpinner) {
                        placeOrderSpinner.classList.add('d-none');
                    }
                    placeOrderButton.disabled = originalButtonDisabled;
                }
            }
            if (!orderSubmissionStarted) {
                isPlaceOrderRunning = false;
            }
        }
    }

    /** Saves remaining Device drafts before navigating to Checkout.
     * @param {MouseEvent} event Click event from the Next link.
     * @returns {Promise<void>}
     */
    async function handleNextStep(event) {
        const nextLink = event.target.closest('[data-order-cart-next]');
        if (!nextLink) {
            return;
        }

        event.preventDefault();
        if (isNextStepRunning) {
            return;
        }

        isNextStepRunning = true;
        const nextButton = nextLink.querySelector('.btn');
        const nextButtonLabel = nextButton.querySelector('[data-order-button-label]');
        const nextButtonSpinner = nextButton.querySelector('[data-order-submit-spinner]');
        const originalButtonText = nextButtonLabel.textContent;
        const errorDisplay = document.querySelector('[data-order-cart-error]');
        let batchControls = [];
        let originalBatchDisabledStates = [];
        let navigationStarted = false;
        nextLink.setAttribute('aria-busy', 'true');
        nextLink.setAttribute('aria-disabled', 'true');
        nextLink.classList.add('pe-none');
        nextButton.setAttribute('aria-disabled', 'true');
        nextButton.classList.add('disabled');
        if (nextButtonSpinner) {
            nextButtonSpinner.classList.remove('d-none');
        }
        if (errorDisplay) {
            errorDisplay.classList.add('d-none');
            errorDisplay.textContent = '';
        }

        try {
            await waitForCartWrites();

            const targets = Object.assign({}, draftCartQuantities);
            if (Object.values(targets).some(function (quantity) {
                return !Number.isInteger(quantity) || quantity < 0;
            })) {
                throw new Error('Check the edited quantities and retry.');
            }

            let result = null;
            if (Object.keys(targets).length > 0) {
                batchControls = Array.from(document.querySelectorAll(
                    '[data-product-stock-card], [data-kit-stock-card]'
                )).flatMap(function (card) {
                    return Array.from(card.querySelectorAll('button, input, select, textarea'));
                });
                originalBatchDisabledStates = batchControls.map(function (control) {
                    return control.disabled;
                });
                batchControls.forEach(function (control) {
                    control.disabled = true;
                });

                const response = await fetch(orderCartData.restUrl, {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        'X-WP-Nonce': window.WP_API_Settings.nonce,
                    },
                    body: JSON.stringify({
                        action: 'batch',
                        quantities: targets,
                    }),
                });
                result = await response.json();

                if (!response.ok || !result.ok || !result.saved || typeof result.cartEmpty !== 'boolean') {
                    throw new Error('Cart changes could not be saved. Please retry.');
                }

                for (const [productId, quantity] of Object.entries(targets)) {
                    if (Number(result.saved[productId]) !== quantity) {
                        throw new Error('Cart changes could not be confirmed. Please retry.');
                    }
                }

                for (const [productId, quantity] of Object.entries(targets)) {
                    savedQuantities[productId] = quantity;
                    delete draftCartQuantities[productId];
                    const card = document.querySelector(
                        '[data-product-id="' + CSS.escape(productId) + '"], [data-kit-id="' + CSS.escape(productId) + '"]'
                    );
                    if (!card) {
                        continue;
                    }

                    const isKit = card.matches('[data-kit-stock-card]');
                    const form = card.querySelector('[data-qty-form]');
                    if (form) {
                        form.dataset.inCartSavedQty = String(quantity);
                        const input = form.querySelector('input[name="quantity"]');
                        if (input) {
                            input.value = String(quantity);
                        }
                    }
                    if (isKit) {
                        card.dataset.kitInCartSavedQty = String(quantity);
                    } else {
                        card.dataset.inCartSavedQty = String(quantity);
                    }

                    if (quantity === 0) {
                        const selectedControls = card.querySelector(
                            isKit ? '[data-kit-selected-controls]' : '[data-selected-product-controls]'
                        );
                        const addForm = card.querySelector('[data-product-add-form]');
                        if (selectedControls) {
                            selectedControls.classList.add('d-none');
                        }
                        if (addForm) {
                            addForm.classList.remove('d-none');
                            const addLabel = addForm.querySelector('[data-add-label]');
                            if (addLabel) {
                                addLabel.textContent = 'Add to cart';
                            }
                        }
                    }
                }
                refreshAllCards();
                updateNextStepAvailability();
            }

            const cartIsEmpty = result ? result.cartEmpty : !Object.values(savedQuantities).some(function (quantity) {
                return Number(quantity) > 0;
            });
            if (cartIsEmpty) {
                throw new Error('Your cart is empty. Add a product before continuing.');
            }

            nextLink.setAttribute('aria-busy', 'true');
            nextLink.setAttribute('aria-disabled', 'true');
            nextLink.classList.add('pe-none', 'disabled');
            nextButton.setAttribute('aria-disabled', 'true');
            nextButton.classList.add('disabled');
            window.location.assign(nextLink.href);
            navigationStarted = true;
        } catch (error) {
            const message = error instanceof TypeError || error instanceof SyntaxError
                ? "We couldn't save your cart changes. Check your connection and try again. Your edits are still on this page."
                : error.message || 'Cart changes could not be saved. Please retry.';
            showCartSaveError(message);
        } finally {
            batchControls.forEach(function (control, index) {
                control.disabled = originalBatchDisabledStates[index];
            });
            if (batchControls.length > 0) {
                refreshAllCards();
            }
            if (!navigationStarted) {
                nextLink.removeAttribute('aria-busy');
                nextLink.classList.remove('pe-none');
                nextButtonLabel.textContent = originalButtonText;
                if (nextButtonSpinner) {
                    nextButtonSpinner.classList.add('d-none');
                }
                isNextStepRunning = false;
                updateNextStepAvailability();
            }
        }
    }

    /** Registers page-level cart handlers and initializes card state.
     * @returns {void}
     */
    function initializeOrderCartPage() {
        document.addEventListener('click', handleQuantityStepButtonClick);
        document.addEventListener('input', handleQuantityInput);
        document.addEventListener('submit', handleProductSet);
        document.addEventListener('submit', handleProductRemove);
        document.addEventListener('submit', handleProductAdd);
        document.addEventListener('submit', handleCheckoutPlaceOrder);
        document.addEventListener('click', handleNextStep);
        refreshAllCards();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initializeOrderCartPage);
    } else {
        initializeOrderCartPage();
    }
}